// src/components/admin/AdminHeader.tsx
import React, { useEffect, useRef, useState } from 'react';
import { Box, IconButton, Avatar, Typography, Menu, MenuItem, Divider, ListItemIcon, Badge, List, ListItem, ListItemText, Paper, Grow } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import AssignmentReturnOutlinedIcon from '@mui/icons-material/AssignmentReturnOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import SearchIcon from '@mui/icons-material/Search';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

interface AdminHeaderProps {
  onToggleMenu: () => void;
}

// Normalize role for color coding (same as in SlideMenu)
const normalizeRole = (role: string): string => {
  const r = role?.toLowerCase().trim() || '';
  if (!r || r === 'new' || r === 'unknown') return 'admin';
  if (r.includes('super')) return 'super_admin';
  if (r === 'operation' || r === 'vendor') return r;
  return 'admin';
};

// Map normalized role to color — kept in sync with SlideMenu's role chip so
// the same role always shows the same color across the whole admin panel.
// Super Admin uses the actual VOTO brand orange-dark rather than a generic
// color, since it's the role shown by default in the admin experience.
const getRoleColor = (normalizedRole: string): string => {
  switch (normalizedRole) {
    case 'super_admin': return 'var(--orange-dark, #e94c1c)'; // Brand orange
    case 'admin':       return '#2196f3'; // Blue
    case 'operation':   return '#ff9800'; // Amber
    case 'vendor':      return '#4caf50'; // Green
    default:            return 'var(--orange, #F76338)'; // Brand orange (fallback)
  }
};

// ---------------------------------------------------------------------------
// Live notification bubbles + chime
// ---------------------------------------------------------------------------
const BUBBLE_TIMEOUT_MS = 10000; // each bubble closes itself after 10 s
const MAX_BUBBLES = 4;
const CHIME_KEY = 'notif_last_chimed_id'; // one chime per notification, even with several tabs open

// Colour + icon per notification type
const bubbleStyle = (type: string) => {
  const t = String(type || '').toLowerCase();
  if (t.includes('priority')) return { color: '#C62828', bg: '#FDECEC', Icon: NotificationsActiveOutlinedIcon };
  if (t.includes('offer')) return { color: '#B26A00', bg: '#FFF4E0', Icon: NotificationsActiveOutlinedIcon };
  if (t.includes('cancel')) return { color: '#C62828', bg: '#FDECEC', Icon: CancelOutlinedIcon };
  if (t.includes('return') || t.includes('refund')) return { color: '#1565C0', bg: '#E8F0FE', Icon: AssignmentReturnOutlinedIcon };
  if (t.includes('order')) return { color: '#1A7A3C', bg: '#E6F7EE', Icon: ShoppingBagOutlinedIcon };
  return { color: '#E94C1C', bg: '#FFF1EB', Icon: NotificationsActiveOutlinedIcon };
};

// Short two-tone "ding" made in the browser (no sound file needed).
// Browsers only allow sound after the person has clicked / typed on the page
// once, so the audio is unlocked on the first click or key press.
let audioCtx: AudioContext | null = null;
const getAudioCtx = (): AudioContext | null => {
  try {
    if (!audioCtx) {
      const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!Ctx) return null;
      audioCtx = new Ctx();
    }
    return audioCtx;
  } catch {
    return null;
  }
};
const unlockAudio = () => {
  const ctx = getAudioCtx();
  if (ctx && ctx.state === 'suspended') ctx.resume().catch(() => {});
};
const playChime = () => {
  const ctx = getAudioCtx();
  if (!ctx) return;
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});
  const start = ctx.currentTime + 0.02;
  [
    { freq: 880, at: 0 },
    { freq: 1318.5, at: 0.16 },
  ].forEach(({ freq, at }) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0.0001, start + at);
    gain.gain.exponentialRampToValueAtTime(0.25, start + at + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + at + 0.45);
    osc.connect(gain).connect(ctx.destination);
    osc.start(start + at);
    osc.stop(start + at + 0.5);
  });
};

// Offer / High-priority broadcasts come from the API with ids like "cust-123".
// Their read state is kept in this browser.
const SEEN_KEY = 'admin_seen_broadcasts';
const isBroadcast = (n: any) => String(n?.id ?? '').startsWith('cust-');
const getSeen = (): string[] => { try { return JSON.parse(localStorage.getItem(SEEN_KEY) || '[]'); } catch { return []; } };
const addSeen = (ids: string[]) => {
  try { localStorage.setItem(SEEN_KEY, JSON.stringify(Array.from(new Set([...getSeen(), ...ids])).slice(-500))); } catch {}
};
const withSeen = (list: any[]) => {
  const seen = new Set(getSeen());
  return list.map((n) => (isBroadcast(n) ? { ...n, is_read: seen.has(String(n.id)) ? 1 : 0 } : n));
};

type NotifTab = 'all' | 'orders' | 'offers' | 'high';
const notifTabOf = (n: any, tab: NotifTab) => {
  const t = String(n?.type || '').toLowerCase();
  if (tab === 'orders') return t.includes('order') || t.includes('cancel') || t.includes('return') || t.includes('refund');
  if (tab === 'offers') return t.startsWith('offer');
  if (tab === 'high') return t.includes('priority');
  return true;
};

const AdminHeader: React.FC<AdminHeaderProps> = ({ onToggleMenu }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationAnchor, setNotificationAnchor] = useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [lastNotificationCheck, setLastNotificationCheck] = useState<string>(() => new Date(Date.now() - 60 * 1000).toISOString());
  const [notificationUnread, setNotificationUnread] = useState(0);
  const [notifTab, setNotifTab] = useState<NotifTab>('all');
  const [notifExpanded, setNotifExpanded] = useState(false); // false = latest 10, true = View more (everything loaded, scrolls)
  const [bubbles, setBubbles] = useState<any[]>([]);
  // ids already seen - null until the first load, so opening / refreshing a
  // page never pops up old notifications, only ones that arrive afterwards
  const seenIdsRef = useRef<Set<number> | null>(null);
  const bubbleTimersRef = useRef<Record<number, number>>({});
  const navigate = useNavigate();
  const open = Boolean(anchorEl);

  // --- Get user info from localStorage (keys set in adminLogin.tsx) ---
  const isVendor = (localStorage.getItem('user_role') || '').toLowerCase() === 'vendor';
  const userName  = (isVendor ? localStorage.getItem('user_name') : localStorage.getItem('admin_name')) || 'Admin';
  const userEmail = (isVendor ? localStorage.getItem('user_email') : localStorage.getItem('admin_email')) || 'admin@example.com';

  // Raw role from API (preserved under 'role')
  const rawRole = localStorage.getItem('role') || localStorage.getItem('user_role') || 'admin';

  // Normalized role for color coding
  const normalizedRole = normalizeRole(rawRole);

  // Avatar letter
  const avatarLetter = userName.charAt(0).toUpperCase() || userEmail.charAt(0).toUpperCase();

  // Format raw role for display (capitalize first letter)
  const displayRole = rawRole
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  useEffect(() => {
    let active = true;
    const loadNotifications = async () => {
      try {
        const token = isVendor ? localStorage.getItem('vendor_token') : localStorage.getItem('admin_token');
        if (!token) return;
        const path = isVendor ? '/admin/vendor-notifications?limit=100' : '/admin/admin-notifications?limit=100';
        const response = await fetch(`${process.env.REACT_APP_SERVER_ENDPOINT}${path}`, { headers: { Authorization: `Bearer ${token}`, authkey: '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3' } });
        const data = await response.json();
        if (active && !data.error) {
          const list: any[] = withSeen(data.notifications || []);
          setNotifications(list);
          setNotificationUnread(Number(data.unread_count || 0) + list.filter((n) => isBroadcast(n) && Number(n.is_read) !== 1).length);
          showNewBubbles(list.filter((n) => !isBroadcast(n)));
        }
      } catch (err) { console.warn('Notification polling failed', err); }
    };
    loadNotifications();
    const timer = window.setInterval(loadNotifications, 5000);
    return () => { active = false; window.clearInterval(timer); };
  }, [isVendor]);

  // sound is allowed only after the first click / key press on the page
  useEffect(() => {
    document.addEventListener('click', unlockAudio);
    document.addEventListener('keydown', unlockAudio);
    return () => {
      document.removeEventListener('click', unlockAudio);
      document.removeEventListener('keydown', unlockAudio);
      Object.values(bubbleTimersRef.current).forEach((t) => window.clearTimeout(t));
    };
  }, []);

  const closeBubble = (id: number) => {
    window.clearTimeout(bubbleTimersRef.current[id]);
    delete bubbleTimersRef.current[id];
    setBubbles((current) => current.filter((b) => b.id !== id));
  };

  // Pop up a bubble (and ding once) for every unread notification that was
  // not there at the previous poll
  function showNewBubbles(list: any[]) {
    if (seenIdsRef.current === null) {
      seenIdsRef.current = new Set(list.map((n) => Number(n.id)));
      return;
    }
    const seen = seenIdsRef.current;
    const fresh = list.filter((n) => !seen.has(Number(n.id)) && Number(n.is_read) !== 1);
    list.forEach((n) => seen.add(Number(n.id)));
    if (!fresh.length) return;

    setBubbles((current) => [...fresh, ...current.filter((b) => !fresh.some((f) => f.id === b.id))].slice(0, MAX_BUBBLES));
    fresh.forEach((n) => {
      bubbleTimersRef.current[n.id] = window.setTimeout(() => closeBubble(n.id), BUBBLE_TIMEOUT_MS);
    });

    // several back office tabs open -> only the first one to see it dings
    const newestId = Math.max(...fresh.map((n) => Number(n.id) || 0));
    let lastChimed = 0;
    try { lastChimed = Number(localStorage.getItem(CHIME_KEY) || 0); } catch {}
    if (newestId > lastChimed) {
      try { localStorage.setItem(CHIME_KEY, String(newestId)); } catch {}
      playChime();
    }
  }

  // Explicit "Mark all as read" (an empty id list = every notification of this user)
  const markAllNotificationsRead = async () => {
    const token = isVendor ? localStorage.getItem('vendor_token') : localStorage.getItem('admin_token');
    const readUrl = isVendor ? '/admin/vendor-notifications/read' : '/admin/admin-notifications/read';
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_ENDPOINT}${readUrl}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, authkey: '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3', 'Content-Type': 'application/json' },
        body: JSON.stringify({ notification_ids: [] }),
      });
      if (response.ok) {
        addSeen(notifications.filter(isBroadcast).map((n: any) => String(n.id)));
        setNotificationUnread(0);
        setNotifications((current) => current.map((n: any) => ({ ...n, is_read: 1 })));
      }
    } catch (err) { console.warn('Failed to mark all notifications as read', err); }
  };

  // Mark one notification read and open the item it is about (bell list and
  // bubbles both use this)
  const openNotification = async (n: any) => {
    const token = isVendor
      ? localStorage.getItem('vendor_token')
      : localStorage.getItem('admin_token');
    const readUrl = isVendor
      ? '/admin/vendor-notifications/read'
      : '/admin/admin-notifications/read';

    try {
      if (isBroadcast(n)) {
        if (Number(n.is_read) !== 1) {
          addSeen([String(n.id)]);
          setNotificationUnread((count) => Math.max(0, count - 1));
          setNotifications((current) => current.map((item: any) => (item.id === n.id ? { ...item, is_read: 1 } : item)));
        }
        throw new Error('__local__');
      }
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_ENDPOINT}${readUrl}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            authkey: '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ notification_ids: [n.id] }),
        }
      );

      if (response.ok && Number(n.is_read) !== 1) {
        setNotificationUnread((count) => Math.max(0, count - 1));
        setNotifications((current) =>
          current.map((item: any) =>
            item.id === n.id ? { ...item, is_read: 1 } : item
          )
        );
      }
    } catch (err: any) {
      if (err?.message !== '__local__') console.warn('Failed to mark notification as read', err);
    }

    // Deep-link straight to the specific item instead of
    // dumping the person on the full, unfiltered list —
    // the destination page opens just that item's detail.
    const et = String(n.entity_type || '');
    if (et === 'priority_product') {
      navigate('/productManagement', { state: { openProductId: n.entity_id } });
    } else if (et === 'priority_delivery') {
      navigate('/deliveryManagement');
    } else if (et === 'offer_deal') {
      navigate('/dealoftheday');
    } else if (et === 'offer_flash') {
      navigate('/flashSale');
    } else if (et.startsWith('offer_')) {
      navigate('/productManagement', { state: { openProductId: n.entity_id } });
    } else if (n.entity_type === 'product') {
      navigate(isVendor ? '/productManagement' : '/vendorProductApprovals', {
        state: { openProductId: n.entity_id },
      });
    } else if (n.entity_type === 'vendor') {
      navigate('/vendorApplications', {
        state: { openVendorId: n.entity_id },
      });
    } else if (n.entity_type === 'guest_order' && !isVendor) {
      // guest order cancellations live on the Guest Orders page
      navigate('/guestOrderlist');
    } else {
      navigate('/adminOrderManagement', {
        state: { openOrderId: n.entity_id, entityType: n.entity_type },
      });
    }
  };

  const tabNotifications = notifications.filter((n: any) => notifTabOf(n, notifTab));

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleProfile = () => {
    handleClose();
    navigate('/adminProfile');
  };

  const handleSettings = () => {
    handleClose();
    navigate('/globalSettings');
  };

  const handleLogout = () => {
    const wasVendor = (localStorage.getItem('user_role') || '').toLowerCase() === 'vendor';

    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_email');
    localStorage.removeItem('admin_name');
    localStorage.removeItem('admin_type');
    localStorage.removeItem('role');
    localStorage.removeItem('admin_role_id');
    localStorage.removeItem('admin_permissions');
    localStorage.removeItem('admin_perm_map');
    localStorage.removeItem('vendor_token');
    localStorage.removeItem('vendor_id');
    localStorage.removeItem('user_email');
    localStorage.removeItem('user_name');
    localStorage.removeItem('user_role');
    localStorage.removeItem('user_id');

    handleClose();
    navigate(wasVendor ? '/vendorlogin' : '/adminLogin');
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: '71px',
        px: 2,
        backgroundColor: 'var(--white, #FFFFFF)',
        borderBottom: '1px solid var(--border, #E8E8E8)',
        boxShadow: 'var(--shadow-sm, 0 2px 12px rgba(0,0,0,0.06))',
        fontFamily: 'var(--ff-body, "Montserrat", sans-serif)',
        position: 'sticky',
        top: 0,
        zIndex: 1100,
      }}
    >
      {/* Left Section */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton
          onClick={onToggleMenu}
          sx={{
            display: { xs: 'flex', md: 'none' },
            color: 'var(--dark, #1A1A1A)',
            transition: 'background-color var(--transition, 0.28s cubic-bezier(0.4,0,0.2,1))',
            '&:hover': { backgroundColor: 'rgba(247,99,56,0.08)' },
          }}
        >
          <MenuIcon />
        </IconButton>

        <Typography
          variant="h6"
          sx={{
            fontFamily: 'var(--ff-display, "Montserrat", sans-serif)',
            fontWeight: 700,
            color: 'var(--dark, #1A1A1A)',
            fontSize: '16px',
            letterSpacing: '-0.01em',
            display: { xs: 'none', sm: 'block' },
          }}
        >
          Admin Panel
        </Typography>
      </Box>

      {/* Right Section */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {/* Role Badge - shows raw role name */}
        <Box
          sx={{
            px: 1.5,
            py: 0.5,
            backgroundColor: getRoleColor(normalizedRole),
            borderRadius: '20px',
            boxShadow: 'var(--shadow-sm, 0 2px 12px rgba(0,0,0,0.06))',
            display: { xs: 'none', sm: 'block' },
          }}
        >
          <Typography 
            variant="caption" 
            sx={{ 
              color: 'white',
              fontFamily: 'var(--ff-display, "Montserrat", sans-serif)',
              fontWeight: 700,
              fontSize: '11px',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}
          >
            {displayRole}
          </Typography>
        </Box>

        <>
            <IconButton
              onClick={(e) => { setNotificationAnchor(e.currentTarget); setNotifExpanded(false); setNotifTab('all'); }}
              aria-label="Notifications"
            >
              <Badge
                badgeContent={notificationUnread > 0 ? notificationUnread : 0}
                color="error"
                max={99}
              >
                <NotificationsNoneIcon />
              </Badge>
            </IconButton>
            <Menu
              anchorEl={notificationAnchor}
              open={Boolean(notificationAnchor)}
              onClose={() => setNotificationAnchor(null)}
              PaperProps={{ sx: { width: 380, maxWidth: 'calc(100vw - 16px)', borderRadius: 2, overflow: 'hidden' } }}
              MenuListProps={{ sx: { p: 0 } }}
            >
              <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid #eee', bgcolor: '#fafafa', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography fontWeight={700}>
                  Notifications{notificationUnread > 0 ? ` (${notificationUnread})` : ''}
                </Typography>
                <Typography
                  component="button"
                  onClick={markAllNotificationsRead}
                  disabled={notificationUnread === 0}
                  sx={{
                    background: 'none', border: 'none', p: '4px 8px', borderRadius: 1, fontSize: 12, fontWeight: 600,
                    color: notificationUnread === 0 ? '#aaa' : 'var(--orange, #F76338)',
                    cursor: notificationUnread === 0 ? 'default' : 'pointer',
                    '&:hover': notificationUnread === 0 ? {} : { background: 'rgba(247,99,56,.1)' },
                  }}
                >
                  Mark all as read
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', borderBottom: '1px solid #f0f0f0' }}>
                {([['all', 'All'], ['orders', 'Orders'], ['offers', 'Offers'], ['high', 'High Priority']] as [NotifTab, string][]).map(([k, label]) => (
                  <Typography
                    key={k}
                    component="button"
                    onClick={() => { setNotifTab(k); setNotifExpanded(false); }}
                    sx={{
                      flex: 1, py: 1, background: 'none', border: 'none', cursor: 'pointer', fontSize: 12,
                      fontWeight: notifTab === k ? 700 : 500,
                      color: notifTab === k ? 'var(--orange, #F76338)' : '#888',
                      borderBottom: `2px solid ${notifTab === k ? 'var(--orange, #F76338)' : 'transparent'}`,
                    }}
                  >
                    {label}
                  </Typography>
                ))}
              </Box>
              {/* scrolls inside the panel; "View more" loads the full list */}
              <List dense disablePadding sx={{ maxHeight: 360, overflowY: 'auto' }}>
                {tabNotifications.length === 0 ? (
                  <ListItem><ListItemText primary="No notifications." /></ListItem>
                ) : (notifExpanded ? tabNotifications : tabNotifications.slice(0, 10)).map((n: any) => (
                  <ListItem
                    key={n.id}
                    button
                    onClick={() => {
                      setNotificationAnchor(null);
                      openNotification(n);
                    }}
                    sx={{ alignItems: 'flex-start', borderBottom: '1px solid #f3f3f3', bgcolor: Number(n.is_read) !== 1 ? '#FFF8F4' : 'transparent' }}
                  >
                    <ListItemText
                      primary={n.title}
                      secondary={
                        <>
                          <span style={{ display: 'block' }}>{n.message}</span>
                          {n.created_at && (
                            <span style={{ display: 'block', fontSize: 10.5, color: '#aaa', marginTop: 2 }}>
                              {new Date(n.created_at).toLocaleString()}
                            </span>
                          )}
                        </>
                      }
                      primaryTypographyProps={{ fontWeight: Number(n.is_read) !== 1 ? 700 : 500, fontSize: 13, color: String(n.type || '').includes('priority') ? '#C62828' : undefined }}
                      secondaryTypographyProps={{ component: 'div', fontSize: 12 }}
                    />
                    {Number(n.is_read) !== 1 && (
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'var(--orange, #F76338)', flexShrink: 0, mt: 1, ml: 1 }} />
                    )}
                  </ListItem>
                ))}
              </List>
              {tabNotifications.length > 10 && (
                <Box sx={{ py: 1, textAlign: 'center', borderTop: '1px solid #eee', bgcolor: '#fafafa' }}>
                  <Typography
                    component="button"
                    onClick={() => setNotifExpanded((v) => !v)}
                    sx={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12.5, fontWeight: 600, color: 'var(--orange, #F76338)' }}
                  >
                    {notifExpanded ? 'View less' : `View more (${tabNotifications.length - 10})`}
                  </Typography>
                </Box>
              )}
            </Menu>
          </>

        <Avatar
          onClick={handleClick}
          sx={{
            width: 32,
            height: 32,
            backgroundColor: getRoleColor(normalizedRole),
            fontFamily: 'var(--ff-display, "Montserrat", sans-serif)',
            fontSize: '14px',
            fontWeight: 700,
            ml: 1,
            cursor: 'pointer',
            boxShadow: 'var(--shadow-sm, 0 2px 12px rgba(0,0,0,0.06))',
            transition: 'opacity var(--transition, 0.28s cubic-bezier(0.4,0,0.2,1))',
            '&:hover': { opacity: 0.85 },
          }}
        >
          {avatarLetter}
        </Avatar>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          onClick={handleClose}
          PaperProps={{
            elevation: 3,
            sx: {
              mt: 1.5,
              minWidth: 240,
              borderRadius: 'var(--radius, 14px)',
              boxShadow: 'var(--shadow-md, 0 8px 32px rgba(0,0,0,0.10))',
              fontFamily: 'var(--ff-body, "Montserrat", sans-serif)',
              '& .MuiMenuItem-root': {
                px: 2,
                py: 1.5,
                fontFamily: 'var(--ff-body, "Montserrat", sans-serif)',
                fontSize: '14px',
                transition: 'background-color var(--transition, 0.28s cubic-bezier(0.4,0,0.2,1))',
                '&:hover': {
                  backgroundColor: 'rgba(247,99,56,0.06)',
                },
              },
            },
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          {/* Profile Info Section */}
          <Box sx={{ px: 2, py: 1.5, cursor: 'default' }}>
            <Typography 
              variant="body2" 
              sx={{ 
                fontFamily: 'var(--ff-display, "Montserrat", sans-serif)',
                fontWeight: 700, 
                color: 'var(--dark, #1A1A1A)',
                mb: 0.5,
              }}
            >
              {userName}
            </Typography>
            <Typography 
              variant="caption" 
              sx={{ 
                color: 'var(--gray, #6B6B6B)',
                display: 'block',
                mb: 0.5,
              }}
            >
              {userEmail}
            </Typography>
            <Box
              sx={{
                display: 'inline-block',
                px: 1,
                py: 0.25,
                backgroundColor: getRoleColor(normalizedRole),
                borderRadius: '20px',
                mt: 0.5,
              }}
            >
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'white',
                  fontFamily: 'var(--ff-display, "Montserrat", sans-serif)',
                  fontWeight: 700,
                  fontSize: '11px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}
              >
                {displayRole}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 1, borderColor: 'var(--border, #E8E8E8)' }} />

          {/* Menu Items */}
          <MenuItem onClick={handleProfile}>
            <ListItemIcon>
              <PersonOutlineIcon fontSize="small" sx={{ color: 'var(--dark, #1A1A1A)' }} />
            </ListItemIcon>
            Your Profile
          </MenuItem>

          <MenuItem onClick={handleSettings}>
            <ListItemIcon>
              <SettingsOutlinedIcon fontSize="small" sx={{ color: 'var(--dark, #1A1A1A)' }} />
            </ListItemIcon>
            Settings
          </MenuItem>

          <Divider sx={{ my: 1, borderColor: 'var(--border, #E8E8E8)' }} />

          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" sx={{ color: '#d72c0d' }} />
            </ListItemIcon>
            <Typography sx={{ color: '#d72c0d', fontWeight: 600, fontFamily: 'var(--ff-body, "Montserrat", sans-serif)' }}>
              Logout
            </Typography>
          </MenuItem>
        </Menu>
      </Box>

      {/* Live notification bubbles (bottom-right). Click one to open the
          item; each closes itself after a few seconds. */}
      <Box
        sx={{
          position: 'fixed',
          right: { xs: 12, sm: 24 },
          bottom: { xs: 12, sm: 24 },
          zIndex: 2000,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          width: { xs: 'calc(100vw - 24px)', sm: 360 },
          pointerEvents: 'none',
        }}
      >
        {bubbles.map((n: any) => {
          const { color, bg, Icon } = bubbleStyle(n.type);
          return (
            <Grow in key={n.id} style={{ transformOrigin: 'bottom right' }}>
              <Paper
                elevation={8}
                role="alert"
                onClick={() => {
                  closeBubble(n.id);
                  openNotification(n);
                }}
                sx={{
                  pointerEvents: 'auto',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  p: 1.75,
                  pr: 1,
                  borderRadius: 3,
                  borderLeft: `5px solid ${color}`,
                  backgroundColor: '#fff',
                  '&:hover': { boxShadow: '0 10px 28px rgba(0,0,0,0.18)' },
                }}
              >
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: '50%',
                    backgroundColor: bg,
                    color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Icon fontSize="small" />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#111', lineHeight: 1.3 }}>
                    {n.title}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 13,
                      color: '#555',
                      mt: 0.25,
                      lineHeight: 1.4,
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      wordBreak: 'break-word',
                    }}
                  >
                    {n.message}
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, color, fontWeight: 600, mt: 0.5 }}>
                    Just now · Click to open
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  aria-label="Close notification"
                  onClick={(e) => {
                    e.stopPropagation();
                    closeBubble(n.id);
                  }}
                  sx={{ mt: -0.5 }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Paper>
            </Grow>
          );
        })}
      </Box>
    </Box>
  );
};

export default AdminHeader;